import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import { formatMessage, parseMessageComponents } from './message-util';

// Get the AIMe server URL from environment variables
const AIME_SERVER_URL = process.env.REACT_APP_AIME_SERVER_URL || 'http://localhost:4000';

// Reuse the existing ALPS token
const getToken = () => localStorage.getItem('idToken');

const ChatInterface = ({
	isTenantUser,
	threadId: externalThreadId,
	setThreadId: setExternalThreadId,
}) => {
	const theme = useTheme();
	const [message, setMessage] = useState('');
	const [chatMessages, setChatMessages] = useState([]); // Start with null thread ID - will be created by server when first message is sent
	// Use external threadId from parent if provided, otherwise manage locally
	const [currentThreadId, setCurrentThreadId] = useState(
		externalThreadId || null
	);

	// Update both local and external state when thread ID changes
	const updateThreadId = (threadId) => {
		setCurrentThreadId(threadId);
		if (setExternalThreadId) {
			setExternalThreadId(threadId);
		}
	};
	const [isLoading, setIsLoading] = useState(false);
	const [connected, setConnected] = useState(false);
	const reconnectAttemptsRef = useRef(0);
	const wsRef = useRef(null);
	const wsSetupCompleted = useRef(false);
	const messagesEndRef = useRef(null);

	// Scroll to bottom of messages
	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		scrollToBottom();
	}, [chatMessages]);

	// Initial load - check user type and set up accordingly
	// TODO: remove eslint disabler and fix dependency issues
	useEffect(() => {
		// Only check user type once on initial load
		checkUserType();

		// Clean up function when component unmounts
		return () => {
			if (wsRef.current) {
				console.log('Component unmounting, closing WebSocket connection');
				wsRef.current.close();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Load messages for a thread using the same pattern as React Native client
	const loadMessagesForThread = (threadId) => {
		setIsLoading(true);

		// Using the same API endpoint as React Native client (from useApi.getThread)
		const url = `${AIME_SERVER_URL}/threads/${threadId}?client_type=web`;

		fetch(url, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${getToken()}`,
				'Content-Type': 'application/json',
			},
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error(`Error fetching thread: ${response.status}`);
				}
				return response.json();
			})
			.then((data) => {
				// Extract messages using exact same pattern as React Native client (ChatScreen.tsx)
				const allMessages = data.thread?.values?.messages || [];

				// Filter out coordinator messages (same as React Native client)
				const filteredMessages = allMessages.filter(
					(message) => message.name !== 'coordinator'
				);

				// Convert server messages to our format, handling content formats as in React Native
				const formattedMessages = filteredMessages.map((msg) => ({
					id: msg.id || `msg_${Date.now()}_${Math.random()}`,
					content: Array.isArray(msg.content)
						? msg.content[0]?.text
						: msg.content,
					isUser: msg.type === 'user',
				}));

				setChatMessages(formattedMessages);
			})
			.catch((error) => {
				console.error('Error loading messages:', error);
				setChatMessages([
					{
						id: `error_${Date.now()}`,
						content: 'Unable to load previous messages.',
						isUser: false,
					},
				]);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	// Effect for handling external thread ID changes
	useEffect(() => {
		// No logging needed here, just monitoring thread ID changes
	}, [externalThreadId, currentThreadId]);

	// Separate effect specifically for loading messages when threadId changes
	useEffect(() => {
		if (externalThreadId) {
			loadMessagesForThread(externalThreadId);
		} else if (externalThreadId === null) {
			// Starting new chat - show welcome message
			setChatMessages([
				{
					id: 'welcome_' + Date.now(),
					content:
						'Hi! I am AIMe, your AI Assistant! How can I help you today?',
					isUser: false,
				},
			]);
		}
	}, [externalThreadId]);

	// Get a thread by ID
	const getThread = async (threadId) => {
		try {
			setIsLoading(true);
			console.log(`=========== FETCHING THREAD ${threadId} ===========`);

			// Exact URL format used in React Native client
			const url = `${AIME_SERVER_URL}/threads/${threadId}?client_type=web`;
			console.log('GET URL:', url);

			// Log all request details for troubleshooting
			const token = getToken();
			console.log(
				'Using token (first 20 chars):',
				token ? token.substring(0, 20) + '...' : 'NO TOKEN'
			);

			const response = await fetch(url, {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
			});

			console.log('Response status:', response.status);
			console.log('Response OK:', response.ok);

			if (!response.ok) {
				// Try an alternative URL format as a fallback
				console.log('First attempt failed, trying alternate URL format');
				const alternateUrl = `${AIME_SERVER_URL}/threads/${threadId}/messages?client_type=web`;
				console.log('GET Alternate URL:', alternateUrl);

				const alternateResponse = await fetch(alternateUrl, {
					method: 'GET',
					headers: {
						Authorization: `Bearer ${token}`,
						'Content-Type': 'application/json',
					},
				});

				console.log('Alternate response status:', alternateResponse.status);

				if (alternateResponse.ok) {
					const alternateData = await alternateResponse.json();
					console.log('Alternate thread data received:', alternateData);

					// Convert messages-only response to thread format
					return {
						thread_id: threadId,
						values: {
							messages: alternateData.messages || [],
						},
					};
				}

				throw new Error(`Error fetching thread: ${response.status}`);
			}

			const data = await response.json();
			console.log('Thread data received:', JSON.stringify(data, null, 2));
			return data.thread;
		} catch (error) {
			console.error('Error fetching thread:', error);
			throw error;
		} finally {
			setIsLoading(false);
		}
	};

	// Load existing messages from a thread - following React Native client pattern with additional debugging
	// TODO: remove leading underscore when actually using, or remove it
	const _loadExistingMessages = async (threadId) => {
		try {
			if (!threadId) {
				console.error('Cannot load messages: threadId is null or undefined');
				return;
			}

			console.log('LOADING MESSAGES FOR THREAD:', threadId);

			// Fetch current thread data from API
			const threadData = await getThread(threadId);
			console.log('Thread data loaded:', JSON.stringify(threadData, null, 2));

			if (!threadData) {
				console.error('Thread data is null or undefined');
				return;
			}

			// Get messages from thread data - trying different possible paths
			let allMessages = [];

			if (threadData.values?.messages) {
				console.log('Found messages in threadData.values.messages');
				allMessages = threadData.values.messages;
			} else if (threadData.messages) {
				console.log('Found messages in threadData.messages');
				allMessages = threadData.messages;
			} else {
				// Try a direct fetch of messages as a fallback
				console.log('No messages found in thread data, trying direct fetch');
				try {
					const response = await fetch(
						`${AIME_SERVER_URL}/threads/${threadId}/messages?client_type=web`,
						{
							method: 'GET',
							headers: {
								Authorization: `Bearer ${getToken()}`,
								'Content-Type': 'application/json',
							},
						}
					);

					if (response.ok) {
						const data = await response.json();
						allMessages = data.messages || [];
						console.log(
							'Direct fetch of messages succeeded:',
							allMessages.length
						);
					} else {
						console.error(
							'Direct fetch of messages failed with status:',
							response.status
						);
					}
				} catch (directFetchError) {
					console.error(
						'Error with direct fetch of messages:',
						directFetchError
					);
				}
			}

			console.log('All messages found:', allMessages.length);
			console.log(
				'Sample message data:',
				allMessages.length > 0
					? JSON.stringify(allMessages[0], null, 2)
					: 'No messages'
			);

			// Filter out coordinator messages (never shown in UI)
			const filteredMessages = allMessages.filter((message) => {
				if (!message) return false;
				return message.name !== 'coordinator';
			});

			// Transform messages to match our format
			const parsedMessages = filteredMessages.map((message) => {
				let messageContent = '';

				if (Array.isArray(message.content) && message.content.length > 0) {
					messageContent = message.content[0]?.text || '';
				} else {
					messageContent = message.content || '';
				}

				return {
					id: message.id || `msg_${Date.now()}_${Math.random()}`,
					content: messageContent,
					isUser: message.type === 'user' || message.role === 'user',
				};
			});

			console.log('Parsed messages:', parsedMessages.length);
			setChatMessages(parsedMessages);
		} catch (error) {
			console.error('Error loading existing messages:', error);
			setChatMessages([
				{
					id: `error_${Date.now()}`,
					content:
						'Unable to load previous messages. Please try again or start a new chat.',
					isUser: false,
				},
			]);
		}
	};

	// Check if user is an Altvia role (without tenant) or tenant user
	const checkUserType = async () => {
		if (isTenantUser) {
			console.log(`Detected tenant role, enabling chat`);
			setupWebSocket();
		}
	};

	// Set up WebSocket connection
	const setupWebSocket = () => {
		const token = getToken();

		if (!token) {
			console.error('No auth token available');
			return;
		}

		// Don't set up WebSocket again if it's already connected or connecting
		if (
			wsRef.current &&
			(wsRef.current.readyState === WebSocket.OPEN ||
				wsRef.current.readyState === WebSocket.CONNECTING)
		) {
			console.log('WebSocket already connected or connecting, skipping setup');
			return;
		}

		// Close existing connection if any
		if (wsRef.current) {
			console.log(
				'Closing existing WebSocket connection before creating a new one'
			);
			wsRef.current.close();
			wsRef.current = null;
		}

		// Add client_type=web parameter to specify web client for token validation
		// Determine WebSocket protocol (wss for https, ws for http)
		let wsBaseUrl;
		if (AIME_SERVER_URL.startsWith('https://')) {
			wsBaseUrl = AIME_SERVER_URL.replace('https://', 'wss://');
		} else {
			wsBaseUrl = AIME_SERVER_URL.replace('http://', 'ws://');
		}
		
		// Construct the full WebSocket URL with token and client type
		const wsUrl = `${wsBaseUrl}/ws?token=${token}&client_type=web`;
		console.log('Connecting to WebSocket as web client');
		wsRef.current = new WebSocket(wsUrl);

		wsRef.current.onopen = () => {
			console.log('WebSocket connected');
			setConnected(true);
			reconnectAttemptsRef.current = 0; // Reset reconnection attempts counter on successful connection
			wsSetupCompleted.current = true; // Mark as successfully set up
		};

		wsRef.current.onmessage = (event) => {
			try {
				const data = JSON.parse(event.data);
				console.log('WebSocket message received:', data);

				// Check for and update thread ID if needed
				if (
					data.threadId &&
					(!currentThreadId || data.threadId !== currentThreadId)
				) {
					console.log(
						'Setting threadId from WebSocket response:',
						data.threadId
					);
					updateThreadId(data.threadId);
				}

				// Process message - handle both 'message' and 'messages/partial' types
				if (data.type === 'message' || data.type === 'messages/partial') {
					console.log('Adding AI message to chat:', data.content);

					// For partial messages, we need to append to the existing message if it's from the same messageId
					if (data.type === 'messages/partial') {
						setChatMessages((prev) => {
							// Try to find an existing message with this messageId
							const existingMessageIndex = prev.findIndex(
								(msg) => msg.messageId === data.messageId
							);

							if (existingMessageIndex >= 0) {
								// Found existing message, update its content
								const newMessages = [...prev];
								newMessages[existingMessageIndex] = {
									...newMessages[existingMessageIndex],
									content: data.content,
									messageId: data.messageId,
								};
								return newMessages;
							} else {
								// No existing message found, create a new one
								// First remove any typing indicators
								const withoutTyping = prev.filter((msg) => !msg.isTyping);
								return [
									...withoutTyping,
									{
										id: `ai_${Date.now()}`,
										content: data.content,
										isUser: false,
										messageId: data.messageId,
									},
								];
							}
						});
					} else {
						// For regular messages, just add them directly
						setChatMessages((prev) => {
							const withoutTyping = prev.filter((msg) => !msg.isTyping);
							return [
								...withoutTyping,
								{
									id: `ai_${Date.now()}`,
									content: data.content,
									isUser: false,
								},
							];
						});
					}
				}
			} catch (error) {
				console.error('Error processing message:', error);
			}
		};

		wsRef.current.onclose = (event) => {
			// event.code 1000 is normal closure
			console.log(`WebSocket disconnected with code ${event.code}`);
			setConnected(false);
			wsSetupCompleted.current = false; // Mark as needing setup

			// Only attempt to reconnect on unexpected closures when user is a tenant user
			if (isTenantUser && event.code !== 1000) {
				// Implement exponential backoff for reconnections
				// Base delay: 3s, 6s, 12s, etc. up to 2 minutes max
				reconnectAttemptsRef.current += 1;
				const maxReconnectDelay = 120000; // 2 minutes
				const baseDelay = 3000; // 3 seconds

				// Calculate delay with exponential backoff
				const reconnectDelay = Math.min(
					baseDelay * Math.pow(2, reconnectAttemptsRef.current - 1),
					maxReconnectDelay
				);

				console.log(
					`Reconnecting WebSocket after ${reconnectDelay / 1000}s delay (attempt ${reconnectAttemptsRef.current})...`
				);
				setTimeout(setupWebSocket, reconnectDelay);
			} else {
				console.log(
					'Not reconnecting WebSocket - user is an Altvia role or normal closure'
				);
			}
		};

		wsRef.current.onerror = (error) => {
			console.error('WebSocket error:', error);
			setConnected(false);
		};
	};

	// Clear the chat history
	// TODO: remove leading underscore when actually using, or remove it
	const _createNewThread = () => {
		// Just clear messages, keep the same thread ID
		setChatMessages([]);
	};

	// Create a new thread - using exactly the same pattern as LWC and React Native clients
	const createThread = async () => {
		try {
			// Note: This is exactly how the LWC client does it
			const response = await fetch(
				`${AIME_SERVER_URL}/threads?client_type=web`,
				{
					method: 'POST',
					headers: {
						Authorization: `Bearer ${getToken()}`,
						'Content-Type': 'application/json',
					},
				}
			);

			if (!response.ok) {
				throw new Error(`Error creating thread: ${response.status}`);
			}

			const data = await response.json();
			console.log('Created new thread:', data);
			const threadId = data.thread.thread_id || data.thread.id;
			setCurrentThreadId(threadId);
			console.log('Set currentThreadId to:', threadId);
			return data.thread;
		} catch (error) {
			console.error('Error creating thread:', error);
			throw error;
		}
	};

	// Send a message - exact copy of how the React Native client does it
	const sendMessage = async (e) => {
		e?.preventDefault();

		if (!message.trim() || !connected) return;

		// Store the current message
		const currentMessage = message;
		setMessage(''); // Clear input field immediately for better UX

		// Add user message to UI immediately
		const newUserMessage = {
			id: `user_${Date.now()}`,
			content: currentMessage,
			isUser: true,
		};

		// Add typing indicator
		const typingIndicator = {
			id: `typing_${Date.now()}`,
			content: '',
			isUser: false,
			isTyping: true,
		};

		setChatMessages((prev) => [...prev, newUserMessage, typingIndicator]);

		try {
			let threadIdToUse = currentThreadId;

			// This is EXACTLY how the React Native client does it in ChatScreen.tsx lines 202-211
			// Create a new thread if one is not currently selected
			if (!threadIdToUse) {
				console.log('No thread ID yet, creating thread via REST API');
				// Call server to create new thread via REST API first
				const newThread = await createThread();

				// Get thread ID from the response
				threadIdToUse = newThread.thread_id || newThread.id;
				updateThreadId(threadIdToUse);
				console.log('New thread created with ID:', threadIdToUse);
			}

			// This is EXACTLY how React Native client sends messages (ChatScreen.tsx lines 213-221)
			if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
				console.log('Sending WebSocket message with thread ID:', threadIdToUse);
				const messageObj = {
					type: 'message',
					threadId: threadIdToUse,
					messageId: `msg_${Date.now()}`,
					content: currentMessage,
				};

				wsRef.current.send(JSON.stringify(messageObj));
			} else {
				throw new Error('WebSocket not connected');
			}
		} catch (error) {
			console.error('Error sending message:', error);
			// Remove typing indicator and show error
			setChatMessages((prev) =>
				prev
					.filter((msg) => !msg.isTyping)
					.concat([
						{
							id: `error_${Date.now()}`,
							content: "Sorry, I'm having trouble connecting to the server.",
							isUser: false,
						},
					])
			);
		}
	};

	return (
		<Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
			{/* Chat area */}
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				{/* Messages */}
				<Box
					sx={{
						flex: 1,
						overflowY: 'auto',
						p: 2,
						display: 'flex',
						flexDirection: 'column',
						gap: 1.5,
					}}
				>
					{isLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress size={40} />
						</Box>
					) : chatMessages.length === 0 ? (
						<Box sx={{ textAlign: 'center', py: 4 }}>
							<Typography color='text.secondary'>
								No messages yet. Start a conversation!
							</Typography>
						</Box>
					) : (
						chatMessages.map((msg) => (
							<Box
								key={msg.id}
								sx={{
									display: 'flex',
									justifyContent: msg.isUser ? 'flex-end' : 'flex-start',
									mb: 1,
								}}
							>
								{msg.isTyping ? (
									// Typing/waiting indicator
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											p: '12px 20px',
											borderRadius: '18px 18px 18px 5px',
											bgcolor:
												theme.palette.mode === 'dark'
													? theme.palette.background.paper
													: '#f5f5f5',
										}}
									>
										{/* Typing indicator that exactly matches LWC implementation */}
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
											}}
										>
											{/* First dot */}
											<Box
												sx={{
													width: 8,
													height: 8,
													margin: '0 2px',
													borderRadius: '50%',
													bgcolor: '#999',
													opacity: 0.6,
													animation: 'typing 1.5s infinite',
													'@keyframes typing': {
														'0%': {
															opacity: 0.6,
															transform: 'scale(1)',
														},
														'50%': {
															opacity: 1,
															transform: 'scale(1.2)',
														},
														'100%': {
															opacity: 0.6,
															transform: 'scale(1)',
														},
													},
												}}
											/>
											{/* Second dot */}
											<Box
												sx={{
													width: 8,
													height: 8,
													margin: '0 2px',
													borderRadius: '50%',
													bgcolor: '#999',
													opacity: 0.6,
													animation: 'typing 1.5s infinite',
													'@keyframes typing': {
														'0%': {
															opacity: 0.6,
															transform: 'scale(1)',
														},
														'50%': {
															opacity: 1,
															transform: 'scale(1.2)',
														},
														'100%': {
															opacity: 0.6,
															transform: 'scale(1)',
														},
													},
													animationDelay: '0.5s',
												}}
											/>
											{/* Third dot */}
											<Box
												sx={{
													width: 8,
													height: 8,
													margin: '0 2px',
													borderRadius: '50%',
													bgcolor: '#999',
													opacity: 0.6,
													animation: 'typing 1.5s infinite',
													'@keyframes typing': {
														'0%': {
															opacity: 0.6,
															transform: 'scale(1)',
														},
														'50%': {
															opacity: 1,
															transform: 'scale(1.2)',
														},
														'100%': {
															opacity: 0.6,
															transform: 'scale(1)',
														},
													},
													animationDelay: '1s',
												}}
											/>
										</Box>
									</Box>
								) : (
									// Regular message
									<Box
										sx={{
											maxWidth: '75%',
											p: '12px 15px',
											borderRadius: msg.isUser
												? '18px 18px 5px 18px'
												: '18px 18px 18px 5px',
											boxShadow: theme.shadows[1],
											bgcolor: msg.isUser
												? theme.palette.primary.main
												: theme.palette.mode === 'dark'
													? theme.palette.background.paper
													: '#f5f5f5',
											color: msg.isUser
												? '#fff'
												: theme.palette.text.primary,
										}}
									>
										<Typography
											variant='body2'
											sx={{
												whiteSpace: 'pre-wrap',
												'& a': { color: theme.palette.primary.main },
												'& code': {
													backgroundColor:
														theme.palette.mode === 'dark'
															? 'rgba(255, 255, 255, 0.1)'
															: 'rgba(0, 0, 0, 0.05)',
													padding: '2px 4px',
													borderRadius: '4px',
												},
												'& pre': {
													backgroundColor:
														theme.palette.mode === 'dark'
															? 'rgba(255, 255, 255, 0.1)'
															: 'rgba(0, 0, 0, 0.05)',
													padding: '8px',
													borderRadius: '4px',
													overflowX: 'auto',
												},
												'& .message-action-button': {
													margin: '4px 8px 4px 0',
													padding: '6px 12px',
													backgroundColor: theme.palette.primary.main,
													color: '#fff',
													border: 'none',
													borderRadius: '4px',
													cursor: 'pointer',
													'&:hover': {
														backgroundColor: theme.palette.primary.dark,
													},
												},
											}}
											dangerouslySetInnerHTML={{
												__html: parseMessageComponents(
													formatMessage(msg.content)
												).text,
											}}
											onClick={(e) => {
												// Handle button clicks
												if (
													e.target.classList.contains(
														'message-action-button'
													)
												) {
													const message =
														e.target.getAttribute('data-message');
													if (message) {
														setMessage(message);
														// Optional: auto-submit the form
														// sendMessage({ preventDefault: () => {} });
													}
												}
											}}
										/>
									</Box>
								)}
							</Box>
						))
					)}
					<div ref={messagesEndRef} />
				</Box>

				{/* Input */}
				<Box
					component='form'
					onSubmit={sendMessage}
					sx={{
						display: 'flex',
						p: 2,
						bgcolor: theme.palette.background.paper,
						borderTop: `1px solid ${theme.palette.divider}`,
					}}
				>
					<TextField
						fullWidth
						placeholder={connected ? 'Type a message...' : 'Connecting...'}
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						variant='outlined'
						size='small'
						disabled={!connected}
						sx={{
							'& .MuiOutlinedInput-root': {
								borderRadius: '25px',
								bgcolor: theme.palette.background.default,
							},
						}}
					/>
					<IconButton
						type='submit'
						color='primary'
						disabled={!connected || !message.trim()}
						sx={{ ml: 1 }}
					>
						<SendIcon />
					</IconButton>
				</Box>
			</Box>
		</Box>
	);
};

export default ChatInterface;
